import { useEffect, useMemo, useState } from 'react';
import {
  Button, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import { AppstoreOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import AppCard from '../../../components/cards/AppCard';
import { createModule, getModules, updateModule } from '../../../services/adminApi';
import '../../../styles/FormGroupsSection.css';

const { Text } = Typography;

const EMPTY_MODULE = { key: '', label: '', collectionName: '', baseModule: '', order: 0, isActive: true };

function ModuleModal({ open, initial, existingModules, onClose, onSave }) {
  const [form] = Form.useForm();
  const isEdit = !!initial?.key;

  useEffect(() => {
    if (open) {
      form.setFieldsValue({ ...EMPTY_MODULE, ...initial });
    }
  }, [open, initial, form]);

  const baseModuleOptions = useMemo(() => (
    existingModules
      .filter((m) => m.key !== initial?.key)
      .map((m) => ({ value: m.key, label: `${m.label || m.key} (${m.key})` }))
  ), [existingModules, initial]);

  const handleSave = async () => {
    const values = await form.validateFields();
    onSave(values);
  };

  return (
    <Modal
      title={isEdit ? 'Edit Module' : 'New Module'}
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      okText="Save Module"
      className="fg-editor-modal"
    >
      <Form form={form} layout="vertical" initialValues={EMPTY_MODULE}>
        <Form.Item
          name="key"
          label="Key"
          tooltip="Lowercase identifier used everywhere this module is referenced (moduleName, admin config lookups). Cannot be changed after creation."
          rules={[{ required: true, message: 'Key is required' }, { pattern: /^[a-z][a-z0-9]*$/, message: 'Lowercase letters and numbers only, starting with a letter' }]}
        >
          <Input placeholder="e.g. sourceCandidates" disabled={isEdit} />
        </Form.Item>
        <Form.Item name="label" label="Label" rules={[{ required: true, message: 'Label is required' }]}>
          <Input placeholder="e.g. Source Candidates" />
        </Form.Item>
        <Form.Item
          name="collectionName"
          label="Collection Name"
          tooltip={'The MongoDB collection that stores this module\'s records. Point it at an existing module\'s collection (e.g. "candidates") to reuse the same data without duplicating it.'}
          rules={[{ required: true, message: 'Collection name is required' }]}
        >
          <Input placeholder="e.g. candidates" />
        </Form.Item>
        <Form.Item
          name="baseModule"
          label="Extends (Base Module)"
          tooltip={'When set, this module inherits every form/column/tab/filter/action config it hasn\'t configured itself from the base module - e.g. "Source Candidates" extending "Candidates" only needs its own List Actions configured.'}
        >
          <Select allowClear showSearch optionFilterProp="label" placeholder="None — standalone module" options={baseModuleOptions} />
        </Form.Item>
        <Form.Item name="order" label="Order">
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        {isEdit && (
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

export default function ModulesSection() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getModules();
      setModules(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error(e.message || 'Failed to load modules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => { setEditingModule(null); setModalOpen(true); };
  const openEdit = (mod) => { setEditingModule(mod); setModalOpen(true); };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      if (editingModule?.key) {
        await updateModule(editingModule.key, values);
        message.success('Module updated');
      } else {
        await createModule(values);
        message.success('Module created');
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      message.error(e.message || 'Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Key', dataIndex: 'key', key: 'key', render: (v) => <Text code>{v}</Text> },
    { title: 'Label', dataIndex: 'label', key: 'label' },
    { title: 'Collection', dataIndex: 'collectionName', key: 'collectionName', render: (v) => <Text type="secondary">{v}</Text> },
    { title: 'Extends', dataIndex: 'baseModule', key: 'baseModule', render: (v) => (v ? <Tag color="blue">{v}</Tag> : <Text type="secondary">—</Text>) },
    { title: 'Order', dataIndex: 'order', key: 'order', width: 80 },
    { title: 'Active', dataIndex: 'isActive', key: 'isActive', width: 90, render: (v) => (v ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>) },
    { title: '', key: 'actions', width: 60, fixed: 'right', render: (_, record) => (
      <Tooltip title="Edit">
        <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(record)} />
      </Tooltip>
    ) },
  ];

  return (
    <div className="fg-section admin-config-section">
      <AppCard
        icon={<AppstoreOutlined />}
        title="Modules"
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Reload</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>New Module</Button>
          </Space>
        )}
      >
        <div className="admin-accent-bar" />
        <Text type="secondary">
          Define workflow modules that reuse another module&apos;s collection and config. A module set to
          &quot;Extends&quot; a base module inherits every form, column, tab, filter, and action config it
          hasn&apos;t configured for itself — override only what&apos;s different (e.g. just its List Actions).
        </Text>
      </AppCard>

      <AppCard>
        <Table
          dataSource={modules}
          rowKey="key"
          columns={columns}
          pagination={false}
          size="small"
          loading={loading}
          className="fg-table admin-config-table"
          locale={{ emptyText: 'No modules yet.' }}
        />
      </AppCard>

      <ModuleModal
        open={modalOpen}
        initial={editingModule}
        existingModules={modules}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
      />
    </div>
  );
}
